import { Button, Tooltip, Typography } from '@mui/material';
import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import type { LedgerEntry } from '../../data/types';
import { date, dateTime, money, units as formatUnits } from '../../lib/format';
import { LEDGER_LABELS } from '../../lib/labels';
import { useRegistry } from '../../store/RegistryStore';
import { useTenantScope } from '../../store/selectors';
import { ConfirmDialog, useToast } from '../../ui/feedback';
import { StatusChip } from '../../ui/primitives';
import DataTable, { type Column } from '../../ui/DataTable';

interface Props {
  entries: LedgerEntry[];
  showHolder?: boolean;
  showFund?: boolean;
  pageSize?: number;
}

const OUTFLOW = new Set([
  'DISTRIBUTION',
  'WITHHOLDING',
  'REDEMPTION',
  'CAPITAL_RETURN',
  'TRANSFER_OUT',
]);

export default function LedgerTable({
  entries,
  showHolder = true,
  showFund = true,
  pageSize = 25,
}: Props) {
  const scope = useTenantScope();
  const { state, dispatch } = useRegistry();
  const toast = useToast();
  const navigate = useNavigate();
  const [reversing, setReversing] = useState<LedgerEntry | null>(null);
  const reversedIds = new Set(state.ledger.map((entry) => entry.reversalOf).filter(Boolean));
  const pendingIds = new Set(
    state.approvals
      .filter((item) => item.kind === 'ADJUSTMENT' && item.status === 'PENDING')
      .map((item) => item.refId),
  );
  const columns: Column<LedgerEntry>[] = [
    {
      key: 'seq',
      label: '#',
      width: 64,
      sortValue: (row) => row.seq,
      render: (row) => (
        <Typography variant="caption" color="text.secondary">
          {row.seq}
        </Typography>
      ),
    },
    {
      key: 'date',
      label: 'Effective',
      sortValue: (row) => row.effectiveDate,
      render: (row) => date(row.effectiveDate),
    },
    {
      key: 'type',
      label: 'Type',
      sortValue: (row) => row.type,
      render: (row) => (
        <>
          <Typography variant="body2" sx={{ fontWeight: 600 }}>
            {LEDGER_LABELS[row.type]}
          </Typography>
          <Typography variant="caption" color="text.secondary">
            {row.memo}
          </Typography>
        </>
      ),
    },
    ...(showHolder
      ? [
          {
            key: 'holder',
            label: 'Holder',
            sortValue: (row: LedgerEntry) => scope.holderById.get(row.holderId)?.name ?? '',
            render: (row: LedgerEntry) => scope.holderById.get(row.holderId)?.name ?? row.holderId,
          },
        ]
      : []),
    ...(showFund
      ? [
          {
            key: 'fund',
            label: 'Fund',
            sortValue: (row: LedgerEntry) => scope.fundById.get(row.fundId)?.code ?? '',
            render: (row: LedgerEntry) =>
              `${scope.fundById.get(row.fundId)?.code ?? ''} · ${scope.classById.get(row.classId)?.code ?? ''}`,
          },
        ]
      : []),
    {
      key: 'units',
      label: 'Units',
      align: 'right',
      sortValue: (row) => row.units,
      render: (row) => (row.units ? `${row.units > 0 ? '+' : ''}${formatUnits(row.units)}` : '—'),
    },
    {
      key: 'amount',
      label: 'Amount',
      align: 'right',
      sortValue: (row) => row.amount,
      render: (row) => (
        <Typography
          variant="body2"
          sx={{ color: OUTFLOW.has(row.type) ? 'text.secondary' : 'text.primary' }}
        >
          {OUTFLOW.has(row.type) ? `(${money(row.amount, true)})` : money(row.amount, true)}
        </Typography>
      ),
    },
    { key: 'ref', label: 'Reference', hideBelow: 'lg', render: (row) => row.reference },
    {
      key: 'by',
      label: 'Posted / approved',
      hideBelow: 'lg',
      render: (row) => (
        <Tooltip title={`Posted ${dateTime(row.postedAt)}`}>
          <Typography variant="caption">
            {row.postedBy}
            {row.approvedBy ? ` / ${row.approvedBy}` : ''}
          </Typography>
        </Tooltip>
      ),
    },
    {
      key: 'reverse',
      label: '',
      align: 'right',
      render: (row) => {
        if (reversedIds.has(row.id))
          return <StatusChip status="REJECTED" tone="neutral" label="Reversed" />;
        if (pendingIds.has(row.id))
          return <StatusChip status="PENDING_APPROVAL" label="Reversal pending" />;
        if (row.type === 'ADJUSTMENT') return null;
        return (
          <Button
            size="small"
            onClick={(event) => {
              event.stopPropagation();
              setReversing(row);
            }}
          >
            Reverse
          </Button>
        );
      },
    },
  ];
  return (
    <>
      <DataTable
        rows={entries}
        columns={columns}
        rowKey={(row) => row.id}
        defaultSort={{ key: 'seq', direction: 'desc' }}
        pageSize={pageSize}
        dense
        onRowClick={
          showHolder ? (row) => navigate(`/manager/holders/sample/${row.holderId}`) : undefined
        }
        emptyMessage="No ledger entries"
      />
      <ConfirmDialog
        open={Boolean(reversing)}
        title={reversing ? `Reverse entry #${reversing.seq}` : ''}
        body={
          reversing
            ? `Posts an equal and opposite entry (${reversing.units ? `${-reversing.units} units, ` : ''}${money(reversing.amount, true)}) once a second person approves. The original entry is kept unchanged.`
            : ''
        }
        confirmLabel="Send for approval"
        requireComment
        commentLabel="Reason for the correction"
        onClose={() => setReversing(null)}
        onConfirm={(reason) => {
          if (reversing)
            dispatch({ type: 'LEDGER_REVERSAL_REQUEST', entryId: reversing.id, reason });
          toast('Reversal sent to Approvals');
          setReversing(null);
        }}
      />
    </>
  );
}
